import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { EDITIONS, editionOutcome } from "@/utils/race-results";
import { Trophy } from "lucide-react";

export function Cloth({ no, className }) {
  return (
    <span
      className={cn(
        "inline-grid h-[22px] min-w-[22px] shrink-0 place-items-center rounded-[5px] bg-gray-100 px-1 text-xs font-extrabold text-gray-900",
        className,
      )}
    >
      {no ?? "–"}
    </span>
  );
}

const PILL_BASE =
  "inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-md border px-2 text-xs font-semibold";

/** How one edition (Early Mail / Race Day / Late Mail) went in a race. */
export function TipPill({ race, edition }) {
  const { kind, n } = editionOutcome(race, edition.key);
  if (kind === "top") {
    return (
      <span
        className={cn(PILL_BASE, "border-brand-pink bg-brand-pink text-white")}
        title={`${edition.label} Top Tip won`}
      >
        <Trophy className="h-3 w-3" aria-hidden="true" />
        {edition.label}
      </span>
    );
  }
  if (kind === "tip") {
    return (
      <span
        className={cn(PILL_BASE, "border-pink-200 bg-[#FFE6F6] text-[#C8007E]")}
        title={`The winner was ${edition.label} tip ${n}`}
      >
        {edition.label} · tip {n}
      </span>
    );
  }
  if (kind === "miss") {
    return (
      <span
        className={cn(
          PILL_BASE,
          "border-transparent bg-gray-100 text-gray-500",
        )}
        title={`${edition.label} tips didn't win`}
      >
        {edition.label}
      </span>
    );
  }
  return (
    <span
      className={cn(
        PILL_BASE,
        "border-dashed border-gray-300 bg-white text-gray-400",
      )}
      title={`No ${edition.label} tips for this race`}
    >
      {edition.label}
    </span>
  );
}

export function EditionPills({ race, className }) {
  return (
    <span className={cn("flex flex-wrap gap-1.5", className)}>
      {EDITIONS.map((edition) => (
        <TipPill key={edition.key} race={race} edition={edition} />
      ))}
    </span>
  );
}

export function PillLegend({ zone }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-gray-500">
      <span
        className={cn(PILL_BASE, "border-brand-pink bg-brand-pink text-white")}
      >
        <Trophy className="h-3 w-3" aria-hidden="true" />
        Top Tip won
      </span>
      <span
        className={cn(PILL_BASE, "border-pink-200 bg-[#FFE6F6] text-[#C8007E]")}
      >
        Winner was tip 2–4
      </span>
      <span
        className={cn(
          PILL_BASE,
          "border-transparent bg-gray-100 text-gray-500",
        )}
      >
        Didn&apos;t win
      </span>
      <span
        className={cn(
          PILL_BASE,
          "border-dashed border-gray-300 bg-white text-gray-400",
        )}
      >
        Not tipped
      </span>
      <span>Prices are Betfair SP · Times {zone}</span>
    </div>
  );
}

/** All 12 tips for a race, three columns (stacked on phones). */
export function TipsDetail({ race }) {
  return (
    <div className="grid gap-x-4 border-t border-dashed border-gray-200 bg-gray-50 px-4 pb-3 sm:grid-cols-3">
      {EDITIONS.map(({ key, label }) => (
        <div key={key}>
          <h5 className="mb-1.5 mt-3 text-[11px] font-black uppercase tracking-wider text-gray-500">
            {label}
          </h5>
          {Array.isArray(race[key]) && race[key].length ? (
            race[key].map((tip, i) => (
              <div
                key={`${key}-${i}`}
                className="flex items-center gap-2 py-1 text-[13.5px]"
              >
                <span className="w-16 shrink-0 text-xs text-gray-500">
                  {i === 0 ? "Top Tip" : `Tip ${i + 1}`}
                </span>
                <Cloth no={tip.no} />
                <span
                  className={cn(
                    "min-w-0 truncate",
                    tip.won && "font-extrabold text-[#C8007E]",
                    tip.scr && "text-gray-400 line-through",
                  )}
                >
                  {tip.name}
                </span>
                {tip.won && (
                  <Trophy
                    className="h-3.5 w-3.5 shrink-0 text-brand-pink"
                    aria-label="Won"
                  />
                )}
                {tip.scr && (
                  <span className="shrink-0 text-xs text-gray-400">
                    Scratched
                  </span>
                )}
              </div>
            ))
          ) : (
            <div className="py-1 text-[13.5px] text-gray-400">
              No {label} for this race
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function ProBand({ compact = false }) {
  const navigate = useNavigate();
  const cta = (
    <button
      type="button"
      onClick={() => navigate("/lexi-pro")}
      className="inline-flex min-h-[42px] items-center justify-center whitespace-nowrap rounded-full bg-brand-pink px-6 text-sm font-bold text-white shadow-[0_0_0_3px_#fff,0_0_0_4px_#F0A7D5] transition-colors hover:bg-[#e391c4]"
      data-gtm-element-id="race_results_pro_button-1"
    >
      Start Lexi Pro $29.95/month
    </button>
  );
  if (compact) {
    return (
      <article className="grid min-w-0 content-start justify-items-start gap-2 rounded-xl border border-pink-200 bg-[#FDF2F8] p-4">
        <span className="text-[13px] font-black uppercase tracking-wide text-brand-pink">
          Lexi Pro
        </span>
        <b className="text-[17px] font-extrabold leading-snug">
          Get the next winner before the jump
        </b>
        <p className="text-[13.5px] text-gray-600">
          All 12 tips per race, including Early Mail and Late Mail.
        </p>
        {cta}
      </article>
    );
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-pink-200 bg-[#FDF2F8] px-5 py-4">
      <div>
        <b className="text-base font-extrabold">
          Get tomorrow&apos;s tips before the jump
        </b>
        <p className="mt-0.5 text-[13.5px] text-gray-600">
          Lexi Pro: all 12 tips per race, including Early Mail and Late Mail.
        </p>
      </div>
      <div className="flex flex-col items-center gap-1.5">
        {cta}
        <small className="text-xs text-gray-600">Cancel anytime</small>
      </div>
    </div>
  );
}

export function DayStatus({ day, label }) {
  if (day.status === "pending") {
    return (
      <div className="animate-pulse rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <div className="h-4 w-48 rounded bg-gray-100" />
        <div className="mt-3 space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-8 rounded bg-gray-50" />
          ))}
        </div>
        <span className="sr-only">Loading {label}</span>
      </div>
    );
  }
  if (day.status === "error") {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600">
        <span>Couldn&apos;t load {label}.</span>
        <button
          type="button"
          onClick={() => day.refetch?.()}
          className="rounded-lg border border-gray-200 px-3 py-1.5 font-semibold text-gray-900 hover:border-[#F0A7D5] hover:text-[#C8007E]"
        >
          Try again
        </button>
      </div>
    );
  }
  return null;
}

export function EmptyState({ children }) {
  return (
    <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center text-sm text-gray-500">
      {children}
    </div>
  );
}
